import { Route, Routes, useNavigate } from "react-router"
import "./app.css"
import BoiloutSelectProductsPage from "@/pages/boilout-select-products"
import BoiloutShiftPage from "@/pages/boilout-form-page"
import { BoiloutPreviousHistoryPage } from "@/pages/boilout-previous-history"
import HomePage from "@/pages/home"
import MobileLayout from "@/layouts/mobile"
import LoginPage from "@/pages/login"
import SelectProductsPage from "@/pages/select-products"
import OnboardShiftPage from "@/pages/onboard-shift"
import DoShiftPage from "@/pages/ipqc-form-page"
import ProtectedRoute from "@/layouts/protected-route"
import ShiftEndedPage from "@/pages/shift-ended"
import NetworkErrorPage from "@/pages/network-error"
import { setNavigate } from "@/services/base"
import { useEffect, useRef } from "react"
import { toast } from "sonner"
import { getVersion } from "@/services/app"
import { posthog } from "@/lib/posthog"
import useStore from "@/store"
import PackagingPage from "@/pages/packaging"
import { LogoutPage } from "@/pages/logout"
import SelectDeptsPage from "@/pages/select-depts"
import { IpqcPreviousHistoryPage } from "@/pages/ipqc-previous-history"
import PackagingHistoryPage from "@/pages/packaging-history"
import PackagingV2Page from "@/pages/packaging-v2"
import PackagingV2HistoryPage from "@/pages/packaging-v2-history"
import ManageProcesses from "@/pages/manage-processes"
import OnboardRMPMPage from "@/pages/onboard-rmpm"
import Rmpm from "@/pages/rmpm"
import { fetchResample } from "./services/rmpm"
import {
  doesOfflineBoiloutBundleMatchPlant,
  doesOfflineBoiloutSessionMatchOwner,
  loadOfflineBoiloutBundle,
  loadOfflineBoiloutSession,
  saveOfflineBoiloutBundle,
  saveOfflineBoiloutSession,
} from "@/boilout/lib/offline-store"
import {
  createOfflineSessionSnapshot as createBoiloutOfflineSessionSnapshot,
  hydrateStoreFromOfflineSession as hydrateBoiloutStoreFromOfflineSession,
} from "@/services/boilout"
import {
  doesOfflineIpqcBundleMatchPlant,
  doesOfflineIpqcSessionMatchOwner,
  loadOfflineIpqcBundle,
  loadOfflineIpqcSession,
  saveOfflineIpqcBundle,
  saveOfflineIpqcSession,
} from "@/ipqc/lib/offline-store"
import {
  createOfflineSessionSnapshot,
  hydrateStoreFromOfflineSession,
} from "@/services/ipqc"

function App() {
  const userStore = useStore.use.user()
  const appStore = useStore.use.app()
  setNavigate(useNavigate())

  useInit()

  function useInit() {
    useEffect(() => {
      void fetchVersion()
    }, [])

    useOfflineIpqcPersistence()
    useOfflineBoiloutPersistence()

    useEffect(() => {
      if (!userStore.token) return;

      const id = setInterval(() => {
        fetchResample();
      }, 600000);

      return () => clearInterval(id);
    }, [userStore.token]);


    useEffect(() => {
      if (userStore.employee.code) {
        posthog.identify(userStore.employee.code, {
          appVersion: appStore.version,
        })
      }

      return () => {
        posthog.reset()
      }
    }, [appStore.version, userStore.employee.code])
  }

  function useOfflineIpqcPersistence() {
    const didHydrateRef = useRef(false)
    const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const hydratedOwnerKeyRef = useRef<string | null>(null)

    useEffect(() => {
      const unsub = useStore.subscribe(() => {
        if (!didHydrateRef.current) {
          return
        }

        if (persistTimeoutRef.current) {
          clearTimeout(persistTimeoutRef.current)
        }

        persistTimeoutRef.current = setTimeout(() => {
          const state = useStore.getState()
          if (!state.user.token) {
            return
          }

          void saveOfflineIpqcBundle(state.ipqc.offline.bundle)

          if (state.ipqc.offline.activeSession) {
            void saveOfflineIpqcSession(createOfflineSessionSnapshot())
          } else {
            void saveOfflineIpqcSession(null)
          }
        }, 150)
      })

      return () => {
        if (persistTimeoutRef.current) {
          clearTimeout(persistTimeoutRef.current)
        }

        unsub()
      }
    }, [])

    useEffect(() => {
      let cancelled = false

      async function hydrateForCurrentUser() {
        const employeeCode = userStore.employee.code
        const plantId = userStore.plant.id
        const ownerKey =
          userStore.token && employeeCode && plantId
            ? `${employeeCode}::${plantId}`
            : null

        if (!ownerKey) {
          hydratedOwnerKeyRef.current = null
          didHydrateRef.current = true
          useStore.getState().ipqc.setOfflineHydrated(true)
          return
        }

        if (hydratedOwnerKeyRef.current === ownerKey) {
          didHydrateRef.current = true
          useStore.getState().ipqc.setOfflineHydrated(true)
          return
        }

        didHydrateRef.current = false
        useStore.getState().ipqc.setOfflineHydrated(false)
        useStore.getState().ipqc.setOfflineBundle(null)
        useStore.getState().ipqc.clearFormData()
        useStore.getState().ipqc.clearOfflineSession()

        try {
          const [bundle, session] = await Promise.all([
            loadOfflineIpqcBundle(),
            loadOfflineIpqcSession(),
          ])

          if (cancelled) {
            return
          }

          if (bundle) {
            if (doesOfflineIpqcBundleMatchPlant(bundle, plantId)) {
              useStore.getState().ipqc.setOfflineBundle(bundle)
            } else {
              await saveOfflineIpqcBundle(null)
            }
          }

          if (session) {
            if (
              doesOfflineIpqcSessionMatchOwner(session, {
                employeeCode,
                plantId,
              })
            ) {
              hydrateStoreFromOfflineSession(session)
            } else {
              await saveOfflineIpqcSession(null)
            }
          }
        } catch (err) {
          console.error("Failed to hydrate offline IPQC state:", err)
        } finally {
          if (cancelled) {
            return
          }

          hydratedOwnerKeyRef.current = ownerKey
          didHydrateRef.current = true
          useStore.getState().ipqc.setOfflineHydrated(true)
        }
      }

      void hydrateForCurrentUser()

      return () => {
        cancelled = true
      }
    }, [userStore.employee.code, userStore.plant.id, userStore.token])
  }

  function useOfflineBoiloutPersistence() {
    const didHydrateRef = useRef(false)
    const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const hydratedOwnerKeyRef = useRef<string | null>(null)

    useEffect(() => {
      const unsub = useStore.subscribe(() => {
        if (!didHydrateRef.current) {
          return
        }

        if (persistTimeoutRef.current) {
          clearTimeout(persistTimeoutRef.current)
        }

        persistTimeoutRef.current = setTimeout(() => {
          const state = useStore.getState()
          if (!state.user.token) {
            return
          }

          void saveOfflineBoiloutBundle(state.boilout.offline.bundle)

          if (state.boilout.offline.activeSession) {
            void saveOfflineBoiloutSession(createBoiloutOfflineSessionSnapshot())
          } else {
            void saveOfflineBoiloutSession(null)
          }
        }, 150)
      })

      return () => {
        if (persistTimeoutRef.current) {
          clearTimeout(persistTimeoutRef.current)
        }

        unsub()
      }
    }, [])

    useEffect(() => {
      let cancelled = false

      async function hydrateForCurrentUser() {
        const employeeCode = userStore.employee.code
        const plantId = userStore.plant.id
        const ownerKey =
          userStore.token && employeeCode && plantId
            ? `${employeeCode}::${plantId}`
            : null

        if (!ownerKey) {
          hydratedOwnerKeyRef.current = null
          didHydrateRef.current = true
          useStore.getState().boilout.setOfflineHydrated(true)
          return
        }

        if (hydratedOwnerKeyRef.current === ownerKey) {
          didHydrateRef.current = true
          useStore.getState().boilout.setOfflineHydrated(true)
          return
        }

        didHydrateRef.current = false
        useStore.getState().boilout.setOfflineHydrated(false)
        useStore.getState().boilout.setOfflineBundle(null)
        useStore.getState().boilout.clearFormData()
        useStore.getState().boilout.clearOfflineSession()

        try {
          const [bundle, session] = await Promise.all([
            loadOfflineBoiloutBundle(),
            loadOfflineBoiloutSession(),
          ])

          if (cancelled) {
            return
          }

          if (bundle) {
            if (doesOfflineBoiloutBundleMatchPlant(bundle, plantId)) {
              useStore.getState().boilout.setOfflineBundle(bundle)
            } else {
              await saveOfflineBoiloutBundle(null)
            }
          }

          if (session) {
            if (
              doesOfflineBoiloutSessionMatchOwner(session, {
                employeeCode,
                plantId,
              })
            ) {
              hydrateBoiloutStoreFromOfflineSession(session)
            } else {
              await saveOfflineBoiloutSession(null)
            }
          }
        } catch (err) {
          console.error("Failed to hydrate offline Boilout state:", err)
        } finally {
          if (cancelled) {
            return
          }

          hydratedOwnerKeyRef.current = ownerKey
          didHydrateRef.current = true
          useStore.getState().boilout.setOfflineHydrated(true)
        }
      }

      void hydrateForCurrentUser()

      return () => {
        cancelled = true
      }
    }, [userStore.employee.code, userStore.plant.id, userStore.token])
  }

  async function fetchVersion() {
    try {
      await getVersion()
    } catch (err) {
      console.error(err)
      toast.error("Error fetching version")
    }
  }

  return (
    <Routes>
      <Route element={<MobileLayout />}>
        <Route index element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/logout" element={<LogoutPage />} />

        <Route element={<ProtectedRoute />}>
          <Route
            path="/boilout/select-products"
            element={<BoiloutSelectProductsPage />}
          />
          <Route path="/ipqc/select-products" element={<SelectProductsPage />} />
          <Route path="/select-departments" element={<SelectDeptsPage />} />
          <Route path="/onboard-shift" element={<OnboardShiftPage />} />
          <Route path="/manage-processes" element={<ManageProcesses />} />
          <Route path="/boilout/form" element={<BoiloutShiftPage />} />
          <Route path="/ipqc/form" element={<DoShiftPage />} />
          <Route
            path="/boilout/histories/:id"
            element={<BoiloutPreviousHistoryPage />}
          />
          <Route
            path="/ipqc/histories/:id"
            element={<IpqcPreviousHistoryPage />}
          />
          <Route path="/packaging/history" element={<PackagingHistoryPage />} />
          <Route path="/shift-ended" element={<ShiftEndedPage />} />
          <Route path="/packaging/shift" element={<PackagingPage />} />

          <Route path="/packaging-v2" element={<PackagingV2Page />} />
          <Route path="/packaging-v2/history" element={<PackagingV2HistoryPage />} />

          <Route path="/rm-pm/onboard" element={<OnboardRMPMPage />} />
          <Route path="/rm-pm" element={<Rmpm />} />
        </Route>

        <Route path="/network-error" element={<NetworkErrorPage />} />
      </Route>
    </Routes>
  )
}

export default App
